import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    ShieldCheck, HeartStraight, LockKey, UsersThree, Sparkle,
    Warning, ArrowRight, ClockCounterClockwise, ShareNetwork, CheckCircle,
    Gift, ArrowSquareOut, Copy,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";

function useLifesign() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const refresh = async () => {
        setLoading(true);
        try {
            const r = await api.get("/lifesign/status");
            setStatus(r.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { refresh(); }, []);
    return { status, loading, refresh };
}

function daysBetween(iso) {
    if (!iso) return 0;
    const d = new Date(iso);
    return Math.max(0, Math.round((d - new Date()) / 86400000));
}

export default function Dashboard() {
    const { user, t, checkAuth } = useApp();
    const nav = useNavigate();
    const { status, refresh } = useLifesign();
    const [vaultCount, setVaultCount] = useState(0);
    const [trustedCount, setTrustedCount] = useState(0);
    const [saving, setSaving] = useState(false);
    const [sub, setSub] = useState(null);
    const [referral, setReferral] = useState(null);

    useEffect(() => {
        // Always refresh user profile on Dashboard mount so recent server
        // state changes (e.g. `activated=true` after payment) reflect instantly.
        checkAuth();
        (async () => {
            try {
                const [v, tr, s, ref] = await Promise.all([
                    api.get("/vault/items"),
                    api.get("/trusted"),
                    api.get("/subscription/status"),
                    api.get("/referral/stats"),
                ]);
                setVaultCount(v.data.length);
                setTrustedCount(tr.data.length);
                setSub(s.data);
                setReferral(ref.data);
            } catch {
                /* soft-fail: widgets show inactive state */
            }
        })();
    }, []);

    const handleAlive = async () => {
        setSaving(true);
        try {
            await api.post("/lifesign/im-alive");
            await refresh();
            toast.success(t("dashboard.imAlive"));
        } catch (e) {
            toast.error("Fehler");
        } finally { setSaving(false); }
    };

    const stateKey = status?.state || "healthy";
    const days = daysBetween(status?.next_deadline_at);
    const totalDays = 30 * (status?.interval_months || 3);
    const progress = Math.min(100, Math.max(0, ((totalDays - days) / totalDays) * 100));

    if (!user) return null;

    return (
        <AppShell>
            <div className="mx-auto max-w-7xl px-6 lg:px-10 py-10 lg:py-14">
                {/* GREETING */}
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-shield-terracotta">
                            {t("dashboard.greeting")}, {user.name || user.email.split("@")[0]}
                        </div>
                        <h1 className="mt-2 font-heading font-bold text-shield-ink text-3xl sm:text-4xl lg:text-5xl tracking-tight" data-testid="dashboard-title">
                            {t("dashboard.youAreProtected")}
                        </h1>
                    </div>
                    {!user.activated && (
                        <Link
                            to="/activate"
                            className="inline-flex items-center gap-2 h-12 px-5 pill bg-shield-terracotta text-white font-semibold hover:bg-shield-terracottaDeep transition-colors"
                            data-testid="dashboard-activate-cta"
                        >
                            <Sparkle size={16} weight="fill" aria-hidden="true" />
                            {t("dashboard.activateNow")}
                        </Link>
                    )}
                </div>

                {/* NORTH STAR: Dead-man switch */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mt-10 card-soft p-8 lg:p-10 grid lg:grid-cols-3 gap-8 items-center relative overflow-hidden"
                    data-testid="deadman-widget"
                >
                    <div className="lg:col-span-2">
                        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-shield-muted">
                            <ShieldCheck size={14} weight="fill" aria-hidden="true" />
                            {t("dashboard.state." + stateKey)}
                        </div>
                        <div className="mt-3 flex items-baseline gap-3">
                            <div className="font-heading font-bold text-6xl lg:text-7xl text-shield-forest tabular-nums" data-testid="days-left">
                                {days}
                            </div>
                            <div className="text-shield-muted font-medium text-lg">
                                {t("dashboard.days")} · {t("dashboard.nextPing")}
                            </div>
                        </div>

                        <div className="mt-6 h-3 rounded-full bg-shield-sage overflow-hidden">
                            <div
                                className="h-full bg-shield-forest transition-all duration-700"
                                style={{ width: `${progress}%` }}
                            />
                        </div>

                        {stateKey !== "healthy" && (
                            <div className="mt-5 flex items-start gap-2 text-shield-terracottaDeep font-medium">
                                <Warning size={20} weight="fill" aria-hidden="true" />
                                <span>Wichtig: Bitte bestätige, dass alles in Ordnung ist.</span>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col items-center gap-3">
                        <button
                            onClick={handleAlive}
                            disabled={saving}
                            className="group w-full inline-flex items-center justify-center gap-2 h-24 px-8 rounded-3xl bg-shield-terracotta text-white text-2xl font-bold shadow-lift hover:bg-shield-terracottaDeep transition-colors active:scale-[0.98] animate-pulse-soft disabled:opacity-70"
                            data-testid="im-alive-btn"
                        >
                            <HeartStraight size={30} weight="fill" aria-hidden="true" />
                            {t("dashboard.imAlive")}
                        </button>
                        <p className="text-sm text-shield-muted text-center">{t("dashboard.imAliveSub")}</p>
                    </div>
                </motion.div>

                {/* GRID: safe / trusted / lifesign / referral */}
                <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <TileLink to="/safe" icon={LockKey} testid="tile-safe"
                        label={t("dashboard.safeItems")} value={vaultCount} tone="forest" />
                    <TileLink to="/trusted" icon={UsersThree} testid="tile-trusted"
                        label={t("dashboard.trustedPersons")} value={trustedCount} tone="terracotta" />
                    <TileLink to="/lifesign" icon={ClockCounterClockwise} testid="tile-lifesign"
                        label={t("dashboard.interval")}
                        value={`${status?.interval_months || 3} ${t("dashboard.months")}`} tone="forest" />
                    <TileLink to="/referral" icon={ShareNetwork} testid="tile-referral"
                        label={t("referral.title")} value={<ArrowRight size={22} weight="bold" />} tone="terracotta" />
                </div>

                {user.activated && (
                    <div className="mt-8 inline-flex items-center gap-2 h-10 px-4 pill bg-shield-success/10 text-shield-success font-semibold" data-testid="activated-badge">
                        <CheckCircle size={16} weight="fill" aria-hidden="true" />
                        {t("dashboard.activated")}
                    </div>
                )}

                {/* --- SaaS transparency widgets --- */}
                <div className="mt-10 grid md:grid-cols-2 gap-6">
                    <SubscriptionWidget sub={sub} t={t} />
                    <GiftReferralWidget referral={referral} t={t} />
                </div>
            </div>
        </AppShell>
    );
}

function SubscriptionWidget({ sub, t }) {
    const active = sub?.activated;
    const days = sub?.active_since_days ?? 0;
    const sinceLabel = active
        ? (days === 0 ? t("subscription.sinceToday") : t("subscription.sinceDays").replace("{days}", days))
        : "";

    return (
        <div className="card-soft p-6 lg:p-7 flex flex-col justify-between gap-4" data-testid="subscription-widget">
            <div>
                <div className="text-xs font-semibold uppercase tracking-[0.15em] text-shield-muted">
                    {t("subscription.title")}
                </div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span
                        className={`inline-flex items-center gap-1.5 h-8 px-3 pill text-sm font-semibold ${active ? "bg-shield-success/10 text-shield-success" : "bg-shield-muted/10 text-shield-muted"}`}
                        data-testid="subscription-status-badge"
                    >
                        <CheckCircle size={14} weight="fill" aria-hidden="true" />
                        {active ? t("subscription.active") : t("subscription.inactive")}
                    </span>
                    {active && (
                        <span className="text-sm text-shield-ink font-medium" data-testid="subscription-since">
                            {sinceLabel}
                        </span>
                    )}
                </div>
            </div>

            {active ? (
                <a
                    href={sub?.manage_url || "https://www.digistore24.com/logins/customer_center/"}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-shield-forest hover:underline w-fit"
                    data-testid="subscription-manage-link"
                >
                    {t("subscription.manage")}
                    <ArrowSquareOut size={14} weight="bold" aria-hidden="true" />
                </a>
            ) : (
                <Link
                    to="/activate"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-shield-terracotta hover:underline w-fit"
                    data-testid="subscription-start-link"
                >
                    <Sparkle size={14} weight="fill" aria-hidden="true" />
                    {t("subscription.startNow")}
                </Link>
            )}
        </div>
    );
}

function GiftReferralWidget({ referral, t }) {
    const link = referral?.share_link || "";
    const invited = referral?.referred_count ?? 0;
    const activated = referral?.activated_count ?? 0;
    const bonusSlots = referral?.bonus_slots ?? 0;

    // Goal-gradient progress:
    // - No invites yet → 0% bar, "Invite 1 friend"
    // - Invited but not activated → 60% bar, near-milestone
    // - Activated (bonus earned) → 100% bar, celebrate + next-goal text
    let progressPct = 0;
    let statusText = t("gift.progressNeedOne");
    if (invited > 0 && invited > activated) {
        progressPct = 60;
        statusText = t("gift.progressPending").replace("{count}", invited - activated);
    } else if (activated > 0) {
        progressPct = 100;
        statusText = t("gift.progressAchieved").replace("{count}", activated);
    }

    const copy = async () => {
        if (!link) return;
        try {
            await navigator.clipboard.writeText(link);
            toast.success(t("gift.copied"));
        } catch {
            toast.error("Fehler");
        }
    };

    return (
        <div className="card-soft p-6 lg:p-7 flex flex-col justify-between gap-4" data-testid="gift-widget">
            <div>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-shield-terracotta">
                    <Gift size={14} weight="fill" aria-hidden="true" />
                    {t("gift.title")}
                </div>
                <p className="mt-2 text-shield-ink leading-relaxed">{t("gift.body")}</p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <button
                    onClick={copy}
                    disabled={!link}
                    className="inline-flex items-center gap-1.5 h-10 px-4 pill bg-shield-forest text-white text-sm font-semibold hover:bg-shield-forestDeep transition-colors disabled:opacity-60"
                    data-testid="gift-copy-btn"
                >
                    <Copy size={14} weight="bold" aria-hidden="true" />
                    {t("gift.copy")}
                </button>
                {referral && (
                    <span className="text-xs text-shield-muted" data-testid="gift-stats">
                        {invited} {t("referral.invited")} · {bonusSlots} {t("referral.bonus")}
                    </span>
                )}
            </div>

            {/* Progress bar + dynamic goal text */}
            <div data-testid="gift-progress" aria-live="polite">
                <div className="flex items-center justify-between text-xs font-semibold text-shield-muted">
                    <span className="uppercase tracking-[0.15em]">{t("gift.goalLabel")}</span>
                    <span className="tabular-nums text-shield-forest" data-testid="gift-progress-pct">
                        {progressPct}%
                    </span>
                </div>
                <div
                    className="mt-2 h-2.5 rounded-full bg-shield-sage/60 overflow-hidden"
                    role="progressbar"
                    aria-valuenow={progressPct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={t("gift.goalLabel")}
                >
                    <motion.div
                        className="h-full rounded-full bg-gradient-to-r from-shield-forest via-shield-success to-shield-terracotta"
                        initial={{ width: 0 }}
                        animate={{ width: `${progressPct}%` }}
                        transition={{ duration: 0.9, ease: "easeOut" }}
                        data-testid="gift-progress-fill"
                    />
                </div>
                <p
                    className="mt-2 text-sm font-medium text-shield-ink leading-snug"
                    data-testid="gift-progress-text"
                >
                    {statusText}
                </p>
            </div>
        </div>
    );
}

function TileLink({ to, icon: Icon, label, value, tone, testid }) {
    const iconBg = tone === "terracotta" ? "bg-shield-terracotta" : "bg-shield-forest";
    return (
        <Link
            to={to}
            className="card-soft p-6 flex flex-col justify-between h-40 hover:-translate-y-1 transition-transform"
            data-testid={testid}
        >
            <div className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${iconBg} text-white`}>
                <Icon size={22} weight="duotone" aria-hidden="true" />
            </div>
            <div>
                <div className="text-xs font-semibold uppercase tracking-[0.15em] text-shield-muted">{label}</div>
                <div className="mt-1 font-heading font-bold text-shield-ink text-2xl">{value}</div>
            </div>
        </Link>
    );
}
